/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { IIconProps, Icon } from '@fluentui/react/lib/Icon';
import { Property } from 'csstype';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { LuisText, LuisTextProps } from 'src/core/components/LuisText';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { getScopedTheme, getStylistV2 } from 'src/core/themes/luisStylist';
import { Entity } from 'src/luis/projects/entities/types/Entity';
import { EntityRole } from 'src/luis/projects/entities/types/EntityRole';
import { SimpleEntity } from 'src/luis/projects/entities/types/SimpleEntity';
import { isMachineLearned } from 'src/luis/projects/entities/utils/entityHelpers';
import { getNDepthPath } from 'src/luis/projects/entities/utils/simpleEntityHelpers/simpleEntityHelpers';
import { ModelType } from 'src/luis/types/ModelType';
import { useBrushEventListeners } from 'src/modules/labeling/hooks/useBrushEventListeners';
import { depthColorsMap } from 'src/modules/labeling/utils/labelingConstants';

export type BrushState = 'noSelection' | 'valid' | 'noInstanceOf' | 'nonMLNoRoleApplied' | 'nonMLNotFired' | 'listEntityNoNormalizedVal';

export type BrushProps = {
    data: { entity?: Entity; parentEntity?: Entity; role?: EntityRole; normalizedValue?: string };
    state: BrushState;
    hidden: boolean;
    attachedToRef: React.MutableRefObject<HTMLElement>;
};

const { styleSpan, styleComponent, styleDiv } = getStylistV2('Brush');
const brushTheme = getScopedTheme('Brush');

const Container = styleSpan(
    'Container',
    (theme, cssVars) => ({
        backgroundColor: theme.containerBackground,
        height: theme.containerHeight,
        boxShadow: theme.containerShadow,
        borderRadius: theme.containerBorderRadius,
        transition: theme.containerTransition,
        zIndex: theme.zIndex as Property.ZIndex,
        whiteSpace: 'nowrap',
        flexDirection: 'row',
        position: 'fixed',
        alignItems: 'center',
        display: cssVars.display
    }),
    {
        cssVars: { display: '' },
        cssVarsSetter: ({ hidden }: { hidden: boolean }) => ({
            display: hidden ? 'none' : 'flex'
        })
    }
);

const BrushIcon = styleComponent<IIconProps>(Icon)('BrushIcon', theme => ({
    fontSize: theme.iconSize,
    margin: theme.iconMargin,
    color: theme.iconColor
}));

const Splitter = styleDiv('Splitter', theme => ({
    height: '100%',
    width: 1,
    backgroundColor: theme.splitterBackground
}));

const Circle = styleSpan(
    'Circle',
    (theme, cssVars) => ({
        height: theme.circleSize,
        width: theme.circleSize,
        border: theme.circleBorder,
        marginLeft: 12,
        borderRadius: '50%',
        backgroundColor: cssVars.backgroundColor
    }),
    {
        cssVars: { backgroundColor: '' },
        cssVarsSetter: ({ depth, modelType }: { depth: number; modelType: ModelType }) => ({
            backgroundColor: isMachineLearned(modelType) ? depthColorsMap[depth] : brushTheme.defaultColor
        })
    }
);

const Text = styleComponent<LuisTextProps>(LuisText)('Text', theme => ({
    margin: theme.textMargin
}));

const messages = defineMessages({
    noMatchingEntity: {
        id: 'labeling.brush.noMatchingEntity',
        defaultMessage: 'No matching entity'
    },
    roleFirst: {
        id: 'labeling.brush.roleFirst',
        defaultMessage: 'Please select a role first'
    },
    normalizedValFirst: {
        id: 'labeling.brush.normalizedValFirst',
        defaultMessage: 'Please select a normalized value first'
    },
    parentFirst: {
        id: 'labeling.brush.parentFirst',
        defaultMessage: 'Please label the parent first'
    },
    constraintNotSatisfied: {
        id: 'labeling.brush.constraintNotSatisfied',
        defaultMessage: 'Constraint ({ instanceOf }) is not satisfied'
    },
    noSelection: {
        id: 'labeling.brush.noSelection',
        defaultMessage: 'Please select an entity from the entity palette'
    }
});

export const Brush = (props: BrushProps) => {
    const { attachedToRef, data, hidden, state } = props;
    const ref = React.useRef<HTMLSpanElement>();
    const { localize } = useLocalization();
    const textProps = { size: brushTheme.textSize, color: brushTheme.textColor };

    useBrushEventListeners({
        attachedToRef,
        ref,
        getCoordinates: React.useCallback(e => ({ top: e.clientY - 38, left: e.clientX - 16 }), [])
    });

    if (!data || !data.entity) {
        return null;
    }

    const content = (() => {
        switch (state) {
            case 'valid': {
                let entityName = data.entity.name;
                let depth = 1;
                let modelType = data.entity.typeId;

                if (data.parentEntity && data.parentEntity.typeId === ModelType.Simple) {
                    const path = getNDepthPath(data.entity as SimpleEntity, data.parentEntity as SimpleEntity);
                    entityName = path.path.join(' / ');
                    depth = path.depth;
                } else if (data.parentEntity && data.parentEntity.typeId === ModelType.Hierarchical) {
                    entityName = `${data.parentEntity.name}::${data.entity.name}`;
                    modelType = data.parentEntity.typeId;
                }

                return (
                    <>
                        <BrushIcon iconName="Highlight" />
                        <Splitter />
                        <Circle customProps={{ depth, modelType }} />
                        <Text {...textProps}>
                            {entityName}
                            {data.role ? `:${data.role.name}` : null}
                            {data.normalizedValue ? `>${data.normalizedValue}` : null}
                        </Text>
                    </>
                );
            }
            case 'nonMLNotFired':
                return <Text {...textProps}>{localize(messages.noMatchingEntity)}</Text>;
            case 'nonMLNoRoleApplied':
                return <Text {...textProps}>{localize(messages.roleFirst)}</Text>;
            case 'listEntityNoNormalizedVal':
                return <Text {...textProps}>{localize(messages.normalizedValFirst)}</Text>;
            case 'noInstanceOf':
                return (
                    <Text {...textProps}>
                        {localize(messages.constraintNotSatisfied, {
                            instanceOf: (data.entity as SimpleEntity).instanceOf?.name
                        })}
                    </Text>
                );
            case 'noSelection':
                return <Text {...textProps}>{localize(messages.noSelection)}</Text>;
            default:
                return <></>;
        }
    })();

    return (
        <Container customProps={{ hidden }} originalRef={ref}>
            {content}
        </Container>
    );
};
